import type { PermissionMapping } from './permissions-types';

// API route permissions: admin APIs
export const ADMIN_API_PERMISSIONS: PermissionMapping[] = [
  // Admin APIs
  {
    path: '/api/admin/users',
    permission: 'admin.view',
    name: 'View Users API',
    description: 'Access to user analytics and search API',
  },
  {
    path: '/api/admin/users/signups',
    permission: 'admin.view',
    name: 'User Signup Trends (Admin)',
    description: 'Signup buckets and period comparisons',
  },
  {
    path: '/api/admin/users/domains',
    permission: 'admin.view',
    name: 'Email Domain Statistics (Admin)',
    description: 'Ranked email domains and type totals',
  },
  {
    path: '/api/admin/users/list',
    permission: 'admin.view',
    name: 'User List (Admin)',
    description: 'Paginated and filtered user profiles',
  },
  {
    path: '/api/admin/users/retention',
    permission: 'admin.view',
    name: 'User Retention API',
    description: 'Weekly signup-cohort retention grid',
  },
  {
    path: '/api/admin/users/engagement',
    permission: 'admin.view',
    name: 'User Engagement API',
    description: 'Daily bounce rate and session length of logged-in users',
  },
  {
    path: '/api/admin/users/[sub]',
    permission: 'admin.view',
    name: 'Manage User API',
    description: 'Hard-delete a user and override their tier',
  },
  {
    path: '/api/admin/crm',
    permission: 'admin.view',
    name: 'CRM API',
    description: 'M&A sales pipeline API',
  },
  {
    path: '/api/admin/crm/[id]',
    permission: 'admin.view',
    name: 'CRM Lead API',
    description: 'M&A sales pipeline single-lead API',
  },
  {
    path: '/api/admin/referrers',
    permission: 'admin.view',
    name: 'View Referrers API',
    description: 'Referrer source statistics API',
  },
  {
    path: '/api/admin/subscriptions',
    permission: 'admin.subscriptions',
    name: 'Manage Subscriptions API',
    description: 'Access to subscriptions API',
  },
  {
    path: '/api/admin/stripe-events',
    permission: 'admin.stripe-events',
    name: 'View Stripe Events API',
    description: 'Access to Stripe events API',
  },

  {
    path: '/api/account',
    permission: null,
    name: 'View Own Account API',
    description: 'Access to own account API (all authenticated users)',
  },
  {
    path: '/api/account/profile',
    permission: null,
    name: 'Update Own Account Profile API',
    description: 'Access to own account profile API (all authenticated users)',
  },
  {
    path: '/api/account/preferences',
    permission: null,
    name: 'Update Own Preferences API',
    description: 'Save own language preference (all authenticated users)',
  },
  {
    path: '/api/tenders',
    permission: null,
    name: 'View Tenders API',
    description: 'Access to TED tender notices API (all authenticated users)',
  },
  {
    path: '/api/admin/settings/maintenance',
    permission: 'admin.view',
    name: 'Manage Maintenance Mode API',
    description: 'Toggle site-wide maintenance mode',
  },
  {
    path: '/api/admin/website',
    permission: 'admin.view',
    name: 'Manage Company Websites API',
    description: 'Access to company website management API',
  },
  {
    path: '/api/admin/overrides',
    permission: 'admin.view',
    name: 'Manage Company Overrides API',
    description: 'Access to company override management API',
  },
  {
    path: '/api/admin/files',
    permission: 'admin.view',
    name: 'Manage Company Files API',
    description: 'Access to company file upload management API',
  },
  {
    path: '/api/admin/coverage',
    permission: 'admin.view',
    name: 'View Coverage API',
    description: 'Access to data coverage metrics API',
  },
  {
    path: '/api/admin/privacy',
    permission: 'admin.view',
    name: 'Manage Private Companies API',
    description: 'Access to private company management API',
  },
  {
    path: '/api/admin/api',
    permission: 'admin.view',
    name: 'Manage API Keys (Admin)',
    description: 'Create and list API keys across all customers',
  },
  {
    path: '/api/admin/api/[id]',
    permission: 'admin.view',
    name: 'Revoke API Key (Admin)',
    description: 'Revoke any API key',
  },
  {
    path: '/api/admin/api/usage',
    permission: 'admin.view',
    name: 'View API Usage (Admin)',
    description: 'Aggregated and per-customer/per-sub API usage',
  },
  {
    path: '/api/admin/api/usage/clients',
    permission: 'admin.view',
    name: 'View API Usage by Client (Admin)',
    description: 'API usage over time split by calling client',
  },
  {
    path: '/api/admin/api/logs',
    permission: 'admin.view',
    name: 'View API Request Log (Admin)',
    description: 'Per-call request log filtered by sub, customer, key or client',
  },
  {
    path: '/api/admin/api/clients',
    permission: 'admin.view',
    name: 'View API Clients (Admin)',
    description: 'Which SDKs, integrations and clients call the API',
  },
  {
    path: '/api/admin/api/metrics/spenders',
    permission: 'admin.view',
    name: 'View Credit Spenders (Admin)',
    description: 'Users ranked by credits spent, API vs web, in a time range',
  },
  {
    path: '/api/admin/api/metrics/spenders/[sub]',
    permission: 'admin.view',
    name: 'View Credit Spend Breakdown (Admin)',
    description: "One user's spend by category, endpoint and day",
  },
  {
    path: '/api/admin/api/metrics/keys',
    permission: 'admin.view',
    name: 'View API Key Activity (Admin)',
    description: 'API keys ranked by requests and credits in a time range',
  },
  {
    path: '/api/admin/logs/search-traffic',
    permission: 'admin.view',
    name: 'Search Traffic (Admin)',
    description: 'Daily engine landings and search console clicks',
  },
  {
    path: '/api/admin/logs/weekday-cycle',
    permission: 'admin.view',
    name: '24h Cycle by Weekday (Admin)',
    description: "Today's hourly activity vs same-weekday averages",
  },
  {
    path: '/api/admin/credits',
    permission: 'admin.view',
    name: 'Grant Credits (Admin)',
    description: 'Grant or reset credits for any sub',
  },
  {
    path: '/api/admin/emails/preview',
    permission: 'admin.view',
    name: 'Preview Emails (Admin)',
    description: 'Render a transactional email template to HTML',
  },
  {
    path: '/api/admin/emails/send-test',
    permission: 'admin.view',
    name: 'Send Test Email (Admin)',
    description: 'Send one template through the real mail transport',
  },
  {
    path: '/api/admin/emails/log',
    permission: 'admin.view',
    name: 'View Sent Email Log (Admin)',
    description: 'List the emails actually sent to users',
  },
  {
    path: '/api/admin/emails/log/[id]',
    permission: 'admin.view',
    name: 'View Sent Email Body (Admin)',
    description: 'Read one logged email as the recipient received it',
  },
  {
    path: '/api/admin/publications/editions',
    permission: 'admin.view',
    name: 'Newsroom Editions (Admin)',
    description: 'List newsroom editions (research article + LinkedIn post)',
  },
  {
    path: '/api/admin/publications/editions/[id]',
    permission: 'admin.view',
    name: 'Newsroom Edition (Admin)',
    description: 'One edition with revisions, assets, checks and the resolved LinkedIn text',
  },
  {
    path: '/api/admin/publications/editions/[id]/assets/[assetId]',
    permission: 'admin.view',
    name: 'Newsroom Edition Asset (Admin)',
    description: 'Image bytes of an edition asset, drafts included',
  },
  {
    path: '/api/admin/credits/grant-all',
    permission: 'admin.view',
    name: 'Bulk Grant Credits (Admin)',
    description: 'Grant credits to every user profile in one call',
  },
  {
    path: '/api/admin/credits/[sub]',
    permission: 'admin.view',
    name: 'Inspect Credits (Admin)',
    description: "View a sub's credit balance and ledger",
  },
  {
    path: '/api/admin/funnel',
    permission: 'admin.view',
    name: 'Funnel Data (Admin)',
    description: 'Weekly conversion counts across the funnel',
  },
  {
    path: '/api/admin/dashboard',
    permission: 'admin.view',
    name: 'Admin Dashboard API',
    description: 'Revenue, visitors, signups and product usage',
  },
  {
    path: '/api/admin/scrape-freshness',
    permission: 'admin.view',
    name: 'Scrape Freshness API',
    description: 'Daily delay from register publication to first record',
  },
  {
    path: '/api/admin/azure-credits',
    permission: 'admin.view',
    name: 'Azure Credits API',
    description: 'Remaining Azure startup credit, expiry and spend',
  },
  {
    path: '/api/admin/credits/reconcile',
    permission: 'admin.view',
    name: 'Reconcile Top-ups (Admin)',
    description: 'Stripe top-up payments with no matching credit grant',
  },
  {
    path: '/api/admin/credits/auto-topups',
    permission: 'admin.view',
    name: 'Auto Top-up Events (Admin)',
    description: 'Recent auto top-up attempts across all users',
  },
  {
    path: '/api/admin/credits/users',
    permission: 'admin.view',
    name: 'List Credit Accounts (Admin)',
    description: 'Paginated users with plan, balance and lifetime spend',
  },
  {
    path: '/api/admin/watchlist',
    permission: 'admin.view',
    name: 'List Watchers (Admin)',
    description: 'Users holding at least one watched company',
  },
  {
    path: '/api/admin/watchlist/[sub]',
    permission: 'admin.view',
    name: 'Inspect Watchlist (Admin)',
    description: 'The companies a given user watches',
  },
];
